import { Button, ExclamationTriangleIcon, ReloadIcon } from '@protoss/ui';
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserMenu } from '../components/UserMenu';
import { usePublicAppConfig } from '../contexts/PublicAppConfigContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import './no-module-page.css';

/**
 * NoModulePage
 *
 * Shown when an authenticated workspace member has no module assigned.
 * Workspace admins can assign modules via Administration → Users.
 *
 * This is not an error page - it is a structured empty state that will
 * resolve once a workspace admin assigns the user a module.
 */
export function NoModulePage() {
  const navigate = useNavigate();
  const { workspace, isLoadingWorkspace, modules, activeModule, refetchModules, refetchWorkspace } =
    useWorkspace();
  const {
    config: { headerWordmark },
  } = usePublicAppConfig();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const effectiveModule = activeModule ?? modules[0] ?? null;
  const isWorkspaceAdmin = workspace?.isAdmin === true;

  React.useEffect(() => {
    if (!effectiveModule) return;
    void navigate(effectiveModule.defaultRoute, { replace: true });
  }, [effectiveModule, navigate]);

  async function handleRefreshAccess() {
    try {
      setIsRefreshing(true);
      await Promise.all([refetchWorkspace(), refetchModules()]);
    } finally {
      setIsRefreshing(false);
    }
  }

  return (
    <div className="no-module-shell">
      <header className="no-module-header">
        <div className="no-module-header-start">
          <span className="no-module-wordmark">{headerWordmark}</span>
          {!isLoadingWorkspace && workspace && (
            <>
              <div className="no-module-header-sep" aria-hidden="true" />
              <span className="no-module-workspace-name">{workspace.name}</span>
            </>
          )}
        </div>
        <div className="no-module-header-end">
          <UserMenu />
        </div>
      </header>

      <main className="no-module-main">
        <div className="no-module-content">
          <div className="no-module-icon" aria-hidden="true">
            <ExclamationTriangleIcon />
          </div>
          <h1 className="no-module-title">No module assigned</h1>
          {isWorkspaceAdmin ? (
            <>
              <p className="no-module-body">
                You are a workspace admin, but your account does not have a module assignment yet.
                Assign yourself a module in workspace user administration to unlock the app shell.
              </p>
              <p className="no-module-body">
                After updating assignments, refresh access here and you will be redirected into the
                first available module automatically.
              </p>
            </>
          ) : (
            <>
              <p className="no-module-body">
                Your account has not been assigned a module for this workspace yet. A workspace
                administrator needs to grant you module access before you can continue.
              </p>
              <p className="no-module-body">
                If your access was just updated, refresh this page state to check again.
              </p>
            </>
          )}
          <div className="no-module-actions">
            {isWorkspaceAdmin && (
              <Button asChild variant="primary" size="md">
                <Link to="/administration/users">Open User Access</Link>
              </Button>
            )}
            <Button
              type="button"
              variant={isWorkspaceAdmin ? 'outline' : 'primary'}
              size="md"
              onClick={() => void handleRefreshAccess()}
              disabled={isRefreshing}
            >
              <ReloadIcon aria-hidden="true" />
              {isRefreshing ? 'Refreshing…' : 'Refresh access'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
